import { Typography } from '@mui/material';
import { FormSelect } from 'components/common/FormSelect';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useGetRecordSummaryMutation } from 'redux/services/dashboard';
import { MONTHS, RECORD_TYPE } from './constants';
import { getYearRange } from './utils';
import './WorkFlowRecord.scss';

export type WorkFlowRecordProps = {
  projectId: string;
  pcDate: string;
  userId: string;
};

export const WorkFlowRecord: React.FC<WorkFlowRecordProps> = ({
  projectId,
  pcDate,
  userId,
}) => {
  const { t } = useTranslation();
  const years = getYearRange(pcDate);
  const [getRecordSummary, { data }] = useGetRecordSummaryMutation();
  const [recordType, setRecordType] = useState({
    ...RECORD_TYPE[0],
    name: t(RECORD_TYPE[0].id),
  });
  const [selectedYear, setSelectedYear] = useState<{ name: string } | null>();

  useEffect(() => {
    if (!selectedYear && years.length) {
      setSelectedYear(years[0]);
    }
  }, [selectedYear, years]);

  useEffect(() => {
    if (projectId && selectedYear) {
      getRecordSummary({
        projectId,
        type: recordType.type,
        year: selectedYear.name,
        userId,
      });
    }
  }, [projectId, recordType, selectedYear, userId, getRecordSummary]);

  const handleSelectRecordType = (recordType: any) => {
    setRecordType(recordType);
  };

  const handleSelectYear = (year: { name: string }) => {
    setSelectedYear(year);
  };

  const formatDisplayedData = (val: number, index: number) => {
    if (val - Math.floor(val) === 0) {
      return `${val}`;
    }

    return '';
  };

  return (
    <div className="workflow-record p-3 d-flex flex-column overflow-auto">
      <div className="d-flex justify-content-between gap-1 flex-column flex-md-row mb-2">
        <div className="d-flex gap-2">
          <Typography className="chart-title nowrap" variant="h6">
            Record Summary
          </Typography>
          <FormSelect
            className="notification__project-select flex-grow-1"
            placeholder="Record Year"
            selectedItem={selectedYear}
            items={years}
            uniqueId="name"
            onSelect={handleSelectYear}
          />
        </div>
        <FormSelect
          className="notification__project-select flex-grow-1"
          placeholder="Record Type"
          selectedItem={recordType}
          items={RECORD_TYPE.map((item) => ({
            ...item,
            name: t(item.id),
          }))}
          uniqueId="id"
          onSelect={handleSelectRecordType}
        />
      </div>

      <ResponsiveContainer width="99%" height="100%">
        <BarChart
          margin={{ left: -10 }}
          barSize={10}
          data={
            data
              ? MONTHS.map((month) => ({
                  month,
                  records: data?.[month] ?? 0,
                }))
              : []
          }
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis dataKey="records" tickFormatter={formatDisplayedData}>
            <Label
              style={{
                textAnchor: 'middle',
                fontSize: '100%',
              }}
              angle={270}
              value={`Nos.`}
            />
          </YAxis>
          <Tooltip />
          <Bar dataKey="records" fill="#604596" radius={[10, 10, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
